import { RouteProp, useRoute } from "@react-navigation/native";
import { useSetAtom } from "jotai";
import React, {
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { SharedValue } from "react-native-reanimated";
import { useQueryLoader } from "react-relay";

import { dateUtils } from "@whoosh/utilities";
import { isDateStringValid } from "@whoosh/utilities/src/dates";
import { usePrevious } from "@whoosh/utilities/src/hooks";

import { ClubStackNavigatorParamList } from "../../../../navigators/ClubNavigator";
import { useFlags } from "../../../../utils/launchDarkly";
import { PlatformSpecificErrorBoundary } from "../../../ErrorHandling/components/PlatformSpecificErrorBoundary";
import { selectedTeeTimesAtom } from "../../atoms";
import { NoAgendaErrorDisplay } from "./components/NoAgendaErrorDisplay";
import {
  TimeSlotListInternal,
  TimeSlotListQueryString,
} from "./components/TimeSlotListInternal";
import {
  TimeSlotListInternalServerTime,
  TimeSlotListInternalServerTimeQueryString,
} from "./components/TimeSlotListInternalServerTime";
import { TimeSlotSkeletonRow } from "./components/TimeSlotSkeletonRow";
import { TimeSlotListInternalQuery } from "./components/__generated__/TimeSlotListInternalQuery.graphql";
import { TimeSlotListInternalServerTimeQuery } from "./components/__generated__/TimeSlotListInternalServerTimeQuery.graphql";

const MAX_EXPIRED_RESTRICTION_REFRESH_RETRIES = 3;
const RESTRICTION_SUBSCRIPTION_FALLBACK_DELAY_MS = 3000;

type TimeSlotListProps = {
  facilityId: string;
  handleRowPress: (slotId: string) => void;
  onJoinableRowPress: (slotId: string) => void;
  currentSlotId?: string | null;
  memberID: string;
  selectedCourseIds: string[];
  setIsScrolled: (val: boolean) => void;
  scrollY?: SharedValue<number>;
  membershipTypeId: string;
  handleRequestedRowPress: (queueSlotId: string, time: string) => void;
  clientClass: "MEMBER" | "CUSTOMER";
};

export const TimeSlotList = ({
  facilityId,
  handleRowPress,
  onJoinableRowPress,
  currentSlotId,
  memberID,
  selectedCourseIds,
  setIsScrolled,
  scrollY,
  membershipTypeId,
  handleRequestedRowPress,
  clientClass,
}: TimeSlotListProps) => {
  const [queryRef, loadQuery] = useQueryLoader<TimeSlotListInternalQuery>(
    TimeSlotListQueryString
  );
  const [serverTimeQueryRef, loadServerTimeQuery] =
    useQueryLoader<TimeSlotListInternalServerTimeQuery>(
      TimeSlotListInternalServerTimeQueryString
    );
  const [serverTimeOffsetMs, setServerTimeOffsetMs] = useState<number | null>(
    null
  );

  const route = useRoute<RouteProp<ClubStackNavigatorParamList, "Agenda">>();
  const setSelectedTeeTimes = useSetAtom(selectedTeeTimesAtom);

  const todaysDate = dateUtils.useTodaysDate();
  const date = route?.params?.date ?? todaysDate;

  const prevDate = usePrevious(date);

  const memberId = memberID;

  const isCustomer = clientClass === "CUSTOMER";
  const { requestConfigs } = useFlags();
  const isAwaitingCustomerMembershipType =
    isCustomer && !!memberId && !membershipTypeId;

  const queryVariables = useMemo(
    () => ({
      facilityId,
      courseIds: selectedCourseIds,
      date,
      memberId,
      membershipTypeId,
      isCustomer,
      requestConfigs: !!requestConfigs,
    }),
    [
      facilityId,
      selectedCourseIds,
      date,
      memberId,
      membershipTypeId,
      isCustomer,
      requestConfigs,
    ]
  );

  const expirationRefreshRetryCountRef = useRef(0);
  const isExpirationRefreshActiveRef = useRef(false);
  const expirationRefreshTimeoutRef = useRef<ReturnType<
    typeof setTimeout
  > | null>(null);
  const [isExpirationRefreshLoading, setIsExpirationRefreshLoading] =
    useState(false);
  const [isCurrentQueryDataReady, setIsCurrentQueryDataReady] = useState(false);

  const clearExpirationRefreshTimeout = useCallback(() => {
    if (expirationRefreshTimeoutRef.current) {
      clearTimeout(expirationRefreshTimeoutRef.current);
      expirationRefreshTimeoutRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (!isAwaitingCustomerMembershipType && date && isDateStringValid(date)) {
      clearExpirationRefreshTimeout();
      isExpirationRefreshActiveRef.current = false;
      expirationRefreshRetryCountRef.current = 0;
      setIsExpirationRefreshLoading(false);
      setIsCurrentQueryDataReady(false);
      setSelectedTeeTimes([]);
      loadQuery(queryVariables, { fetchPolicy: "network-only" });
      loadServerTimeQuery({}, { fetchPolicy: "network-only" });
    }
  }, [
    date,
    loadQuery,
    setSelectedTeeTimes,
    isAwaitingCustomerMembershipType,
    queryVariables,
    clearExpirationRefreshTimeout,
    loadServerTimeQuery,
  ]);

  useEffect(() => {
    return () => {
      clearExpirationRefreshTimeout();
    };
  }, [clearExpirationRefreshTimeout]);

  const refreshExpiredRestrictions = useCallback(() => {
    if (
      isAwaitingCustomerMembershipType ||
      !date ||
      !isDateStringValid(date) ||
      expirationRefreshTimeoutRef.current
    ) {
      return;
    }

    setIsExpirationRefreshLoading(true);
    loadQuery(queryVariables, { fetchPolicy: "network-only" });
    loadServerTimeQuery({}, { fetchPolicy: "network-only" });
  }, [
    date,
    isAwaitingCustomerMembershipType,
    loadQuery,
    queryVariables,
    loadServerTimeQuery,
  ]);

  const handleRestrictionRefreshDue = useCallback(() => {
    if (isExpirationRefreshActiveRef.current) {
      return;
    }

    isExpirationRefreshActiveRef.current = true;
    expirationRefreshRetryCountRef.current = 0;
    clearExpirationRefreshTimeout();
    expirationRefreshTimeoutRef.current = setTimeout(() => {
      expirationRefreshTimeoutRef.current = null;
      refreshExpiredRestrictions();
    }, RESTRICTION_SUBSCRIPTION_FALLBACK_DELAY_MS);
  }, [clearExpirationRefreshTimeout, refreshExpiredRestrictions]);

  const handleRestrictionSubscriptionReceived = useCallback(() => {
    if (!isExpirationRefreshActiveRef.current) {
      return;
    }

    clearExpirationRefreshTimeout();
    isExpirationRefreshActiveRef.current = false;
    expirationRefreshRetryCountRef.current = 0;
    setIsExpirationRefreshLoading(false);
  }, [clearExpirationRefreshTimeout]);

  const handleTeeSheetDataLoaded = useCallback(
    (hasExpiredRestrictions: boolean) => {
      setIsCurrentQueryDataReady(true);

      if (!isExpirationRefreshActiveRef.current) {
        return;
      }

      setIsExpirationRefreshLoading(false);

      if (
        hasExpiredRestrictions &&
        expirationRefreshRetryCountRef.current <
          MAX_EXPIRED_RESTRICTION_REFRESH_RETRIES
      ) {
        expirationRefreshRetryCountRef.current += 1;
        clearExpirationRefreshTimeout();
        expirationRefreshTimeoutRef.current = setTimeout(() => {
          expirationRefreshTimeoutRef.current = null;
          refreshExpiredRestrictions();
        }, 1000);
        return;
      }

      isExpirationRefreshActiveRef.current = false;
      expirationRefreshRetryCountRef.current = 0;
    },
    [clearExpirationRefreshTimeout, refreshExpiredRestrictions]
  );

  return (
    <PlatformSpecificErrorBoundary
      fallback={(args: any) => {
        // Set this so we can clear the error from above.
        if (date !== prevDate) {
          args.resetError();
        }
        return <NoAgendaErrorDisplay />;
      }}
    >
      <>
        <Suspense fallback={null}>
          {serverTimeQueryRef ? (
            <TimeSlotListInternalServerTime
              queryRef={serverTimeQueryRef}
              onServerTimeOffsetLoaded={setServerTimeOffsetMs}
            />
          ) : null}
        </Suspense>
        <Suspense fallback={<TimeSlotSkeletonRow />}>
          {queryRef ? (
            <TimeSlotListInternal
              queryRef={queryRef}
              serverTimeOffsetMs={serverTimeOffsetMs}
              date={date}
              handleRowPress={handleRowPress}
              onJoinableRowPress={onJoinableRowPress}
              handleRequestedRowPress={handleRequestedRowPress}
              currentSlotId={currentSlotId}
              courseIds={selectedCourseIds}
              setIsScrolled={setIsScrolled}
              scrollY={scrollY}
              membershipTypeId={membershipTypeId}
              isCustomer={isCustomer}
              isExpirationRefreshLoading={isExpirationRefreshLoading}
              isCurrentQueryDataReady={isCurrentQueryDataReady}
              onRestrictionRefreshDue={handleRestrictionRefreshDue}
              onRestrictionSubscriptionReceived={
                handleRestrictionSubscriptionReceived
              }
              onTeeSheetDataLoaded={handleTeeSheetDataLoaded}
            />
          ) : (
            <TimeSlotSkeletonRow />
          )}
        </Suspense>
      </>
    </PlatformSpecificErrorBoundary>
  );
};
